import { Box, Spinner, useBreakpointValue } from "@chakra-ui/react"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import { useQueryParamState } from "@pathwright/ux/src/components/augmentation/useQueryParamHistory"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { useMemo } from "react"
import { useHistory } from "react-router-dom"
import { useLibraryResources } from "../library/browse/useLibraryResources"
import { getStoreResourceUrl } from "../utils/urls"
import { COVER_PAGE_CLOSE_TARGET_STATE_KEY } from "./coverPageCloseTarget"
import { COVER_PAGE_PANEL_ROOT_KEY } from "./constants"
import { useCoverPageContext } from "./CoverPageProvider"
import { useCoverPageTranslate } from "./utils"

const CoverPageLibraryNavigation = () => {
  const { t } = useCoverPageTranslate()
  const history = useHistory()
  const { queryParamPath } = useQueryParamState("panel")
  const {
    paintResource,
    resourceId,
    loadingNextCoverPage,
    setLoadingNextCoverPage,
    closeTarget,
    immersiveActive,
    queriesAreLoading,
    isDesignMode
  } = useCoverPageContext()
  const libraryResources = useLibraryResources()
  const courses = libraryResources?.courses || ([] as any[])
  const isMobile = useBreakpointValue({
    base: true,
    md: false
  })

  // Find current course index in the courses array
  const currentIndex = useMemo(() => {
    if (!courses?.length) return -1

    const currentResourceId = String(resourceId)
    const resourceSlug = paintResource?.slug || null

    return courses.findIndex((course) => {
      const courseId = course?.id != null ? String(course.id) : null
      if (courseId && courseId === currentResourceId) return true
      if (resourceSlug && course?.slug === resourceSlug) return true
      return false
    })
  }, [resourceId, paintResource?.slug, courses])

  // Get next and previous courses
  const { prevCourse, nextCourse } = useMemo(() => {
    if (!courses?.length) {
      return { prevCourse: null, nextCourse: null }
    }

    if (currentIndex === -1) {
      // Keep navigation available even if current course is not in the loaded window.
      // This can happen when library resources are paged/filtered.
      return {
        prevCourse: courses[courses.length - 1],
        nextCourse: courses[0]
      }
    }

    const prevIndex = currentIndex > 0 ? currentIndex - 1 : courses.length - 1
    const nextIndex = currentIndex < courses.length - 1 ? currentIndex + 1 : 0

    return {
      prevCourse: courses[prevIndex],
      nextCourse: courses[nextIndex]
    }
  }, [currentIndex, courses])

  const prevPathname = useMemo(
    () => (prevCourse ? getStoreResourceUrl(prevCourse.slug, true) : null),
    [prevCourse]
  )

  const nextPathname = useMemo(
    () => (nextCourse ? getStoreResourceUrl(nextCourse.slug, true) : null),
    [nextCourse]
  )

  const navigationState = useMemo(
    () =>
      closeTarget ? { [COVER_PAGE_CLOSE_TARGET_STATE_KEY]: closeTarget } : {},
    [closeTarget]
  )

  // Each resource has its own offers/contributors/categories. Keep the editor
  // open at its overview rather than carrying an item from the previous page.
  const search =
    isDesignMode && /^\/?cover(?:\/|#|$)/.test(queryParamPath || "")
      ? `?${new URLSearchParams({ panel: `/${COVER_PAGE_PANEL_ROOT_KEY}` })}`
      : ""

  const handlePrevClick = () => {
    if (
      prevPathname &&
      prevCourse &&
      !loadingNextCoverPage &&
      !queriesAreLoading
    ) {
      setLoadingNextCoverPage?.(true)
      history.push({ pathname: prevPathname, search, state: navigationState })
    }
  }

  const handleNextClick = () => {
    if (
      nextPathname &&
      nextCourse &&
      !loadingNextCoverPage &&
      !queriesAreLoading
    ) {
      setLoadingNextCoverPage?.(true)
      history.push({ pathname: nextPathname, search, state: navigationState })
    }
  }

  useKeyControl("ArrowLeft", handlePrevClick, { ignoreEditableTarget: true })
  useKeyControl("ArrowRight", handleNextClick, { ignoreEditableTarget: true })

  // Don't render on mobile
  if (isMobile) {
    return null
  }

  // Don't render if we don't have navigation options
  if (!prevCourse && !nextCourse) {
    return null
  }

  // Don't render in immersive mode
  if (immersiveActive) {
    return null
  }

  const ArrowButton = ({ direction }: { direction: "prev" | "next" }) => {
    const pathname = direction === "prev" ? prevPathname : nextPathname
    if (!pathname) {
      return null
    }

    const linkDisabled = loadingNextCoverPage || queriesAreLoading

    return (
      <Box
        position="fixed"
        top="50%"
        transform="translateY(-50%)"
        left={direction === "prev" ? "10px" : undefined}
        right={direction === "next" ? "10px" : undefined}
        zIndex={12}
      >
        <Tooltip
          title={
            (direction === "prev" ? prevCourse.name : nextCourse.name) || ""
          }
        >
          <Link
            to={{ pathname, search, state: navigationState }}
            aria-label={
              direction === "prev"
                ? t("library_navigation.previous_course")
                : t("library_navigation.next_course")
            }
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            height={10}
            width={10}
            minWidth={10}
            minHeight={10}
            bgColor="whiteAlpha.600"
            backdropFilter="blur(20px)"
            borderRadius="full"
            textDecoration="none"
            pointerEvents={linkDisabled ? "none" : undefined}
            tabIndex={linkDisabled ? -1 : undefined}
            aria-disabled={linkDisabled || undefined}
            opacity={loadingNextCoverPage ? 0.7 : undefined}
            cursor={linkDisabled ? "not-allowed" : undefined}
            _hover={{
              bgColor: linkDisabled ? undefined : "whiteAlpha.800"
            }}
            _active={{
              bgColor: linkDisabled ? undefined : "whiteAlpha.900"
            }}
            onClick={(e) => {
              if (linkDisabled) {
                e.preventDefault()
                return
              }
              if (!e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
                setLoadingNextCoverPage?.(true)
              }
            }}
          >
            {loadingNextCoverPage ? (
              <Spinner size="sm" />
            ) : (
              <Pathicon
                icon={`chevron-${direction === "prev" ? "left" : "right"}`}
                color="blackAlpha.600"
                size="24px"
                style={{
                  marginLeft: direction === "prev" ? "-2px" : undefined,
                  marginRight: direction === "next" ? "-2px" : undefined
                }}
              />
            )}
          </Link>
        </Tooltip>
      </Box>
    )
  }

  return (
    <>
      {/* Previous Arrow */}
      {prevCourse && <ArrowButton direction="prev" />}
      {nextCourse && <ArrowButton direction="next" />}
    </>
  )
}

export default CoverPageLibraryNavigation
